import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { SUPPORTED_LANGUAGES, getLanguageByCode } from '../../config/languages';

/**
 * LanguageSwitcher Component
 * 
 * Dropdown menu for switching between supported languages
 * Maintains the current page path when switching languages
 */
const LanguageSwitcher = () => {
    const { lang } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const currentLanguage = getLanguageByCode(lang);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (newLangCode) => {
        // Get current path without language prefix
        const pathWithoutLang = location.pathname.split('/').slice(2).join('/');

        // Build new path with new language
        const newPath = `/${newLangCode}${pathWithoutLang ? '/' + pathWithoutLang : ''}${location.search}${location.hash}`;

        // Navigate to new language version
        navigate(newPath);
        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Language Selector Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    flex items-center gap-2 px-4 py-2.5 rounded-lg 
                    border-2 transition-all duration-300
                    ${isOpen
                        ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200'
                        : 'bg-white text-gray-700 border-gray-200 hover:border-blue-500 hover:shadow-md hover:shadow-blue-100'
                    }
                `}
                aria-label="Select language"
            >
                <span className="text-xl leading-none">{currentLanguage.flag}</span>
                <span className="text-sm font-semibold">
                    {currentLanguage.code.toUpperCase()}
                </span>
                <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M19 9l-7 7-7-7"
                    />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-2xl border-2 border-gray-100 py-2 z-50 origin-top-right transform transition-all animate-fadeIn">
                    {SUPPORTED_LANGUAGES.map((language) => (
                        <button
                            key={language.code}
                            onClick={() => handleLanguageChange(language.code)}
                            className={`
                                w-full flex items-center gap-3 px-4 py-3 text-left 
                                transition-all duration-200 group
                                ${language.code === lang
                                    ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                                    : 'text-gray-700 hover:bg-gray-50 border-l-4 border-transparent hover:border-blue-300'
                                }
                            `}
                        >
                            <span className="text-2xl leading-none">{language.flag}</span>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold truncate">{language.nativeName}</div>
                                <div className="text-xs text-gray-500 truncate">{language.name}</div>
                            </div>
                            {language.code === lang && (
                                <svg
                                    className="w-5 h-5 text-blue-600 flex-shrink-0"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;
